import { JssStyle } from 'jss';
import { createSurveyStyles } from '@sm/webassets';
import { SurveyThemeType } from '@sm/webassets/dist/components/SurveyTheme';

import { isPartial, isCenter, isPartialRight, isBanner, getLayoutSize, LayoutSizeName } from '~app/helpers/themeLayout';

type LayoutStyleClasses =
  | 'layoutContainer'
  | 'layoutPartialContainer'
  | 'layoutContentContainer'
  | 'layoutTopContainer'
  | 'layoutMainContainer'
  | 'layoutLogoContainer'
  | 'layoutToolbarContainer';

type LayoutStyleProps = { isRTL: boolean };
type LayoutSize = Record<LayoutSizeName, JssStyle>;

const partialSize: LayoutSize = {
  full: {
    width: '0%',
    height: 0,
    minHeight: 'auto',
  },
  top: {
    width: '100%',
  },
  half: {
    width: '0%',
    '@media (min-width: 601px)': {
      width: '34%',
    },
    '@media (min-width: 769px)': {
      width: '50%',
    },
  },
  third: {
    width: '0%',
    '@media (min-width: 601px)': {
      width: '34%',
    },
  },
};
const imageSize: LayoutSize = {
  ...partialSize,
  full: {
    ...partialSize.full,
    width: '100%',
  },
};
const contentSize: LayoutSize = {
  full: {
    width: '100%',
  },
  top: {
    width: '100%',
  },
  half: {
    width: '100%',
    '@media (min-width: 601px)': {
      width: '66%',
    },
    '@media (min-width: 769px)': {
      width: '50%',
    },
  },
  third: {
    width: '100%',
    '@media (min-width: 601px)': {
      width: '66%',
    },
  },
};

const useStyles = createSurveyStyles<LayoutStyleClasses, LayoutStyleProps>((theme: SurveyThemeType) => {
  const partialBgImage = [
    `linear-gradient(${theme?.surveyPage?.overlayColor}, ${theme?.surveyPage?.overlayColor})`,
    theme?.surveyPage?.backgroundImage,
  ]
    .filter(o => typeof o !== 'undefined')
    .join(', ');
  return {
    layoutContainer: {
      direction: ({ isRTL }) => (isRTL ? 'rtl' : 'ltr'),
      display: 'flex',
      flexDirection: isBanner(theme.layout) ? 'column' : 'row',
    },
    layoutPartialContainer: {
      position: 'relative',
      order: isPartialRight(theme.layout) ? 2 : 1,
      minHeight: '40px',
      ...partialSize[getLayoutSize(theme.layout)],

      '&:before': {
        display: 'block',
        position: isBanner(theme.layout) ? 'absolute' : 'fixed',
        content: '""',
        zIndex: -10,
        top: 0,
        height: '100%',
        background: {
          position: '50% 50%',
          size: 'cover',
          image: partialBgImage,
        },
        ...imageSize[!isPartial(theme.layout) ? 'full' : getLayoutSize(theme.layout)],
      },
    },
    layoutContentContainer: {
      boxSizing: 'border-box',
      color: theme?.questionColor,
      fontFamily: theme?.questionTitle?.fontFamily,
      minHeight: '100%',
      backgroundColor: isPartial(theme.layout) ? theme?.primaryBackgroundColor : undefined,
      order: isPartialRight(theme.layout) ? 1 : 2,
      ...contentSize[getLayoutSize(theme.layout)],
    },
    layoutTopContainer: {},
    layoutMainContainer: {
      maxWidth: '896px',
      margin: isCenter(theme.layout) || isBanner(theme.layout) ? '0 auto' : {},
      padding: [40, 24, 0, 24],
      '@media (min-width: 321px)': {
        padding: [40, 40, 0, 40],
      },
      '@media (min-width: 601px)': {
        padding: [80, 80, 0, 80],
      },
      '@media (min-width: 769px)': {
        padding: [80, 140, 0, 140],
      },
    },

    layoutLogoContainer: {
      padding: isBanner(theme.layout) ? [40, 40] : [8, 40, 0, 40],
    },
    layoutToolbarContainer: {
      display: 'flex',
      flexDirection: 'row',
      // May change depend on RTL?
      justifyContent: 'flex-end',
      alignItems: 'center',
      boxSizing: 'border-box',
      padding: [12, 12, 0, 0],
      width: '100%',
      '& > *:not(:first-child)': {
        // May change depend on RTL?
        marginLeft: '10px',
      },
    },
  };
});

export default useStyles;
